import React, { useContext, useMemo, useState } from 'react'

import CustomLink from '@ors/components/ui/Link/Link'
import PermissionsContext from '@ors/contexts/PermissionsContext'

import { Menu, MenuItem } from '@mui/material'
import { MdExpandMore } from 'react-icons/md'
import { GoDatabase } from 'react-icons/go'
import { formatApiUrl, longDownloadUrl } from '@ors/helpers'
import { ListingProjectData } from '@ors/components/manage/Blocks/ProjectsListing/interfaces.ts'

const GenerateDBMenu = ({
  projectData,
}: {
  projectData: ListingProjectData
}) => {
  const { canViewProjects, canApproveProjects, isMlfsUser } =
    useContext(PermissionsContext)

  const menuItems = useMemo(
    () => [
      {
        title: 'MYA warehouse',
        url: formatApiUrl(
          '/api/projects/v2/export/?category=Multi-year+agreement',
        ),
        permissions: [canViewProjects],
      },
      {
        title: 'Inventory report',
        url: longDownloadUrl('/api/projects/v2/export/?inventory_report=true'),
        permissions: [canViewProjects],
      },
      {
        title: 'Projects warehouse',
        url: '/projects-listing/export/all',
        permissions: [canViewProjects],
      },
      {
        title: 'Approval summary',
        url: '/projects-listing/approval-summary',
        permissions: [isMlfsUser && canApproveProjects],
      },
      {
        title: 'Summary of projects',
        url: '/projects-listing/summary-of-projects',
        permissions: [isMlfsUser && canApproveProjects],
      },
      {
        title: 'List of projects and activities',
        url: '/projects-listing/blanket-approval-details',
        permissions: [isMlfsUser && canViewProjects],
      },
      {
        title: 'Compare versions',
        url: '/projects-listing/compare-versions',
        permissions: [canViewProjects],
      },
      {
        title: 'Enterprise warehouse',
        url: null,
        permissions: [isMlfsUser && canViewProjects],
      },
      {
        title: 'Associated projects',
        url: projectData.projectId
          ? formatApiUrl(
              `/api/projects/v2/export_associated_projects?project_id=${projectData.projectId}`,
            )
          : null,
        onClick: (evt: MouseEvent) => {
          const proceed = confirm(
            `Do you want to generate the report on associated projects for ${projectData.projectCode}?`,
          )
          if (!proceed) {
            evt.preventDefault()
          }
        },
        permissions: [canViewProjects],
      },
    ],
    [
      canApproveProjects,
      canViewProjects,
      isMlfsUser,
      projectData.projectCode,
      projectData.projectId,
    ],
  )

  const filteredMenuItems = menuItems.filter(
    ({ permissions }) => !permissions || permissions.some(Boolean),
  )

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)

  const handleOpen = (event: React.MouseEvent<HTMLDivElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  return (
    <>
      {filteredMenuItems.length > 0 && (
        <div className="flex cursor-pointer gap-1 px-2" onClick={handleOpen}>
          <GoDatabase className="mb-1" size={18} />
          Generate DB
          <MdExpandMore size={16} color="black" className="mt-0.5" />
        </div>
      )}

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        disablePortal
        keepMounted
        TransitionProps={{
          timeout: 0,
        }}
        MenuListProps={{ disablePadding: true }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        disableAutoFocusItem
        slotProps={{
          paper: {
            elevation: 4,
            sx: {
              borderRadius: '10px !important',
              mt: 1,
              minWidth: 180,
              maxWidth: 240,
              boxShadow: '0px 2px 10px rgba(0,0,0,0.08)',
            },
          },
        }}
      >
        {filteredMenuItems.map(({ url, title, onClick }) => (
          <MenuItem
            key={title}
            className="whitespace-normal rounded-none p-0 hover:bg-white"
            onClick={handleClose}
            disabled={!url}
          >
            <CustomLink
              className="h-full w-full break-words py-2 pl-3.5 pr-7 text-lg normal-case leading-tight tracking-[0.05em] no-underline"
              href={url}
              variant="contained"
              onClick={onClick}
            >
              {title}
            </CustomLink>
          </MenuItem>
        ))}
      </Menu>
    </>
  )
}

export default GenerateDBMenu
